// ClydeFlow theme engine — supports three modes:
//   "light"  → always daylight
//   "dark"   → always night
//   "auto"   → follows the real sun: light between local sunrise & sunset, dark otherwise
//
// We persist the chosen MODE, plus the last RESOLVED theme (so the pre-paint
// inline script in index.html can apply the right class instantly, no flash).
// Auto mode uses the device's geolocation (cached) to compute true sunrise/
// sunset; if location is unavailable it falls back to civil hours (07:00–19:00).

export const MODE_KEY = "clydeflow-theme-mode";
export const RESOLVED_KEY = "clydeflow-theme-resolved";
export const COORDS_KEY = "clydeflow-geo";
const LEGACY_KEY = "clydeflow-theme";

const safeGet = (k) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const safeSet = (k, v) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage blocked */
  }
};

export const getMode = () => {
  const m = safeGet(MODE_KEY);
  if (m === "light" || m === "dark" || m === "auto") return m;
  // Migrate the old binary toggle key, if present.
  const legacy = safeGet(LEGACY_KEY);
  if (legacy === "light" || legacy === "dark") return legacy;
  // Default to auto — light by day, dark after sunset.
  return "auto";
};

export const setMode = (mode) => safeSet(MODE_KEY, mode);

export const getCoords = () => {
  try {
    const c = JSON.parse(safeGet(COORDS_KEY));
    if (c && typeof c.lat === "number" && typeof c.lng === "number") return c;
  } catch {
    /* ignore */
  }
  return null;
};

export const setCoords = (lat, lng) =>
  safeSet(COORDS_KEY, JSON.stringify({ lat, lng }));

// --- Sunrise / sunset (NOAA-based, no external deps; adapted from SunCalc) ---
const rad = Math.PI / 180;
const dayMs = 86400000;
const J1970 = 2440588;
const J2000 = 2451545;
const toJulian = (d) => d.valueOf() / dayMs - 0.5 + J1970;
const fromJulian = (j) => new Date((j + 0.5 - J1970) * dayMs);
const toDays = (d) => toJulian(d) - J2000;
const e = rad * 23.4397;

const solarMeanAnomaly = (d) => rad * (357.5291 + 0.98560028 * d);
const eclipticLongitude = (M) =>
  M +
  rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M)) +
  rad * 102.9372 +
  Math.PI;
const declination = (l) => Math.asin(Math.sin(l) * Math.sin(e));
const julianCycle = (d, lw) => Math.round(d - 0.0009 - lw / (2 * Math.PI));
const approxTransit = (Ht, lw, n) => 0.0009 + (Ht + lw) / (2 * Math.PI) + n;
const solarTransitJ = (ds, M, L) =>
  J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
const hourAngle = (h, phi, d) =>
  Math.acos(
    (Math.sin(h) - Math.sin(phi) * Math.sin(d)) / (Math.cos(phi) * Math.cos(d))
  );

function sunTimes(date, lat, lng) {
  const lw = rad * -lng;
  const phi = rad * lat;
  const d = toDays(date);
  const n = julianCycle(d, lw);
  const ds = approxTransit(0, lw, n);
  const M = solarMeanAnomaly(ds);
  const L = eclipticLongitude(M);
  const dec = declination(L);
  const Jnoon = solarTransitJ(ds, M, L);
  const w0 = hourAngle(rad * -0.833, phi, dec); // sunrise/sunset altitude
  if (Number.isNaN(w0)) return null; // polar day/night
  const Jset = solarTransitJ(approxTransit(w0, lw, n), M, L);
  const Jrise = Jnoon - (Jset - Jnoon);
  return { sunrise: fromJulian(Jrise), sunset: fromJulian(Jset) };
}

// Returns "light" | "dark" for the current moment, given the active mode.
export const resolveTheme = (mode = getMode()) => {
  if (mode === "light") return "light";
  if (mode === "dark") return "dark";
  // auto
  const now = new Date();
  const coords = getCoords();
  if (coords) {
    const t = sunTimes(now, coords.lat, coords.lng);
    if (t) return now >= t.sunrise && now < t.sunset ? "light" : "dark";
  }
  // Fallback when we have no location: simple daytime window.
  const h = now.getHours();
  return h >= 7 && h < 19 ? "light" : "dark";
};

export const applyTheme = (resolved) => {
  document.documentElement.classList.toggle("theme-light", resolved === "light");
  safeSet(RESOLVED_KEY, resolved);
};

// Best-effort one-time geolocation request (used the first time Auto is active
// without cached coordinates). Resolves true if coords were obtained.
export const requestGeo = () =>
  new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords(pos.coords.latitude, pos.coords.longitude);
        resolve(true);
      },
      () => resolve(false),
      { maximumAge: 6 * 60 * 60 * 1000, timeout: 8000, enableHighAccuracy: false }
    );
  });
